// src/pages/UtilityAllowance.jsx
//
// Utility Allowance tab — HousingAI COMPUTED, cited schedule.
// Resolves the active org off `currentUser` (logged-in org slug) and fetches
// its CURRENT schedule from the compliance service DB (written by
// emitUtilityData.js) — the same row the PDF endpoint renders, so screen and
// download can never disagree. Renders as a table that mirrors the 52667 form.
//
// Unlike a plain figure grid, every rate carries provenance and every fuel row
// can be expanded to show the source URL + the exact math used per bedroom.
// That is the whole point: nothing on this page is an uncited number.
//
// PRINT / PDF: the Print button calls window.print(). The @media print CSS
// below hides the app and swaps in a purpose-built paginated document
// (PrintableSchedule, bottom of this file):
//   Page 1+  — schedule grid (full HUD-52667 row set, flows as needed)
//   Next     — HUD-52667 "Actual Family Allowances" fill-in worksheet
//   Next     — rate sources + methodology (24 CFR 982.517 / 10% threshold)
//   Last     — full per-bedroom calculation detail, two columns
// "Save as PDF" in the print dialog produces the deliverable. Users should
// turn OFF "Headers and footers" and turn ON "Background graphics".
//
// DATA CONTRACT (per org):
//   { org, locality, provider, code, computedDate, modelNote, title, unitsLabel,
//     authority?, ahdd?, acdd?,            // used by the print header
//     bedrooms: ['0 BR', ...],
//     rates: { <rateKey>: { label, value, unit, fixed?, effectiveDate, source, url } },
//     categories: [
//       { unitType, structureIdx?, rows: [
//         { label, rateKey?, values: [...],
//           // optional per-bedroom derivation, same length as `bedrooms`:
//           breakdown?: [ { native, nativeUnit, monthly } | null, ... ] }
//       ]}
//     ] }
//
// If `rateKey` + a matching entry in `rates` exist, the row gets a "show math"
// toggle. `breakdown` is optional; without it the toggle still shows the rate
// + formula, just not the plugged-in per-cell numbers.

import React, { useState, useEffect, useCallback } from 'react';
import { createPortal } from 'react-dom';
import {
  Zap, FileText, Printer, Calculator, ClipboardList,
  ChevronDown, ChevronRight, ExternalLink,
  RefreshCw, AlertTriangle,
} from 'lucide-react';

// UA PDF endpoint lives in housing-compliance-service (same base the ACOP
// tab calls). Set this to that service's base URL — match whatever the ACOP
// fetches use — or override per-env with REACT_APP_COMPLIANCE_API.
const COMPLIANCE_API =
  process.env.REACT_APP_COMPLIANCE_API ||
  (window.location.hostname === 'localhost'
    ? 'http://localhost:3200'
    : 'https://housing-compliance-service.onrender.com');

const formatDate = (iso) => {
  if (!iso) return null;
  const [y, m, d] = iso.split('-').map(Number);
  if (!y || !m || !d) return iso;
  return new Date(y, m - 1, d).toLocaleDateString('en-US', {
    year: 'numeric', month: 'long', day: 'numeric',
  });
};

// "$2.0688/therm + $12.00/mo fixed" — always at least 2 decimals ($8.7 -> $8.70)
const formatRateValue = (v) => {
  const s = String(v);
  const dec = s.includes('.') ? s.split('.')[1].length : 0;
  return dec < 2 ? Number(v).toFixed(2) : s;
};
const formatRate = (r) => {
  if (!r) return '';
  const base = `$${formatRateValue(r.value)}${r.unit || ''}`;
  return r.fixed ? `${base} + $${Number(r.fixed).toFixed(2)}/mo fixed` : base;
};

// "Salem Housing Authority - MA055" -> "Salem Housing Authority"
// (code prints in its own pill; same trick as the PDF's deriveAuthority)
const displayLocality = (d) => {
  let a = d?.locality || '';
  if (d?.code && a.endsWith(` - ${d.code}`)) a = a.slice(0, -(` - ${d.code}`.length));
  return a;
};

const UtilityAllowance = ({ currentUser, forceSample = false }) => {
  // Schedule comes from the compliance service DB — single source of truth,
  // same data the PDF endpoint renders. No static copy to drift.
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState('');

  const slug = (currentUser || '').toLowerCase();

  const loadSchedule = useCallback(async () => {
    if (!slug) { setLoading(false); return; }
    setLoading(true); setLoadError('');
    try {
      const res = await fetch(`${COMPLIANCE_API}/api/utility-allowance/${encodeURIComponent(slug)}`);
      if (res.status === 404) { setData(null); return; }
      if (!res.ok) throw new Error(`schedule ${res.status}`);
      const body = await res.json();
      // forceSample: the /sample route shows the same schedule with the
      // SAMPLE presentation (banner, footer marks), regardless of org flag.
      const sched = body.schedule || null;
      setData(sched && forceSample ? { ...sched, sample: true } : sched);
    } catch (err) {
      console.error('UtilityAllowance load:', err);
      setLoadError('Failed to load your utility allowance schedule.');
    } finally {
      setLoading(false);
    }
  }, [slug, forceSample]);

  useEffect(() => { loadSchedule(); }, [loadSchedule]);

  const [showRates, setShowRates] = useState(false);
  const [showApplianceSources, setShowApplianceSources] = useState(false);
  const [showReview, setShowReview] = useState(false);
  // keep-mode: 'adopted' (kept + revised values) vs 'computed' (full HUSM
  // reference at current rates). Only orgs with an adopted schedule toggle.
  const [scheduleView, setScheduleView] = useState('adopted');
  const [openRows, setOpenRows] = useState(() => new Set());
  const [downloading, setDownloading] = useState(false);

  // Server-rendered PDF from the compliance service: the org's CURRENT
  // schedule straight from the DB (single source of truth), identical for
  // every user, real page footers, no print-dialog settings involved.
  // Falls back to window.print() (PrintableSchedule below) if unreachable.
  const downloadPdf = useCallback(async (summary = false) => {
    if (!data || downloading) return;
    setDownloading(true);
    try {
      const params = [summary ? 'format=summary' : null, forceSample ? 'sample=1' : null].filter(Boolean).join('&');
      const res = await fetch(
        `${COMPLIANCE_API}/api/utility-allowance/${encodeURIComponent(data.org || (currentUser || '').toLowerCase())}/pdf${params ? `?${params}` : ''}`
      );
      if (!res.ok) throw new Error(`PDF service ${res.status}`);
      const blob = await res.blob();
      const cd = res.headers.get('Content-Disposition') || '';
      const m = cd.match(/filename="?([^";]+)"?/);
      const a = document.createElement('a');
      a.href = URL.createObjectURL(blob);
      a.download = m ? m[1] : `utility-allowance-${data.org || 'schedule'}-${summary ? 'summary' : 'complete'}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(a.href);
    } catch (err) {
      console.error('PDF download failed, falling back to print:', err);
      window.print();
    } finally {
      setDownloading(false);
    }
  }, [data, currentUser, downloading]);

  const toggleRow = useCallback((key) => {
    setOpenRows((prev) => {
      const next = new Set(prev);
      next.has(key) ? next.delete(key) : next.add(key);
      return next;
    });
  }, []);

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mb-4" />
        <p className="text-gray-500">Loading utility allowance schedule...</p>
      </div>
    );
  }
  if (loadError) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-6 text-center">
        <AlertTriangle className="h-10 w-10 text-red-500 mx-auto mb-3" />
        <p className="text-red-700 mb-4">{loadError}</p>
        <button onClick={loadSchedule} className="px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 inline-flex items-center gap-2">
          <RefreshCw className="h-4 w-4" /> Retry
        </button>
      </div>
    );
  }
  if (!data) {
    return (
      <div className="bg-gray-50 border border-gray-200 rounded-xl p-12 text-center">
        <FileText className="h-12 w-12 text-gray-400 mx-auto mb-4" />
        <h3 className="text-lg font-semibold text-gray-700 mb-2">No utility allowance on file</h3>
        <p className="text-gray-500">Contact your HousingAI representative to add your schedule.</p>
      </div>
    );
  }

  const { bedrooms, rates = {} } = data;
  const effective = formatDate(data.effectiveDate);
  const nCols = bedrooms.length;

  return (
    <div className="space-y-4">
      {/* Print-only paginated document (invisible on screen, portals to <body>) */}
      <PrintableSchedule data={data} />

      {/* Sample banner — unmissable on landing; real customers never see it */}
      {data.sample && (
        <div className="bg-amber-50 border border-amber-300 rounded-xl px-4 py-3 flex items-start gap-2.5">
          <AlertTriangle className="h-5 w-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div className="text-sm text-amber-900">
            <span className="font-bold">Sample schedule.</span>{' '}
            This is a demonstration of HousingAI&rsquo;s utility allowance product — not an
            adopted schedule of this housing authority. Your authority&rsquo;s officially
            adopted schedule governs.
          </div>
        </div>
      )}

      {/* Header card */}
      <div className="bg-white rounded-xl p-5 shadow-sm border border-gray-100">
        <div className="flex flex-wrap items-start justify-between gap-3">
          <div className="min-w-0">
            <div className="flex items-center gap-2">
              <div className="p-2 rounded-lg bg-blue-50">
                <Zap className="h-5 w-5 text-blue-700" />
              </div>
              <h3 className="text-lg font-bold text-gray-900">{data.title}</h3>
            </div>
            <p className="text-sm text-gray-500 mt-1">
              {displayLocality(data)} · {data.provider}
            </p>
            <div className="flex flex-wrap items-center gap-2 mt-2">
              {data.code && (
                <span className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-mono">
                  {data.code}
                </span>
              )}
              {data.sample && (
                <span
                  className="px-2 py-0.5 bg-amber-100 text-amber-800 text-xs rounded-full font-semibold"
                  title="Prepared for demonstration — not an adopted schedule of this housing authority"
                >
                  SAMPLE
                </span>
              )}
              {data.ahdd && (
                <span
                  className="px-2 py-0.5 bg-gray-100 text-gray-700 text-xs rounded-full font-mono"
                  title="Annual heating degree days for this authority — HUD HUSM per-PHA climate dataset (30-year NOAA normals)"
                >
                  {data.ahdd}
                </span>
              )}
              <span className="px-2 py-0.5 bg-gray-100 text-gray-600 text-xs rounded-full">
                {data.unitsLabel}
              </span>
              {effective && (
                <span className="px-2 py-0.5 bg-blue-100 text-blue-700 text-xs rounded-full">
                  Effective {effective}
                </span>
              )}
            </div>
          </div>
          <div className="flex items-center gap-2">
            <button
              onClick={() => downloadPdf(true)}
              disabled={downloading}
              title="Condensed one-page chart (legal size) — the complete schedule remains the schedule of record"
              className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
            >
              <Printer className="h-4 w-4" /> {downloading ? 'Generating…' : 'Summary PDF'}
            </button>
            <button
              onClick={() => downloadPdf(false)}
              disabled={downloading}
              title="Complete schedule of record — rate sources, 10% review, and full calculation detail"
              className="flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded hover:bg-gray-50 disabled:opacity-60"
            >
              <Printer className="h-4 w-4" /> {downloading ? 'Generating…' : 'Complete PDF'}
            </button>
          </div>
        </div>

        {/* Cited rate panel (collapsible) */}
        {Object.keys(rates).length > 0 && (
          <div className="mt-4 border-t border-gray-100 pt-3">
            <button
              onClick={() => setShowRates((v) => !v)}
              className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
            >
              {showRates ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
              Rate sources ({Object.keys(rates).length + (data.ahdd ? 1 : 0)}) — every figure traceable
            </button>
            {showRates && (
              <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                {Object.entries(rates).map(([key, r]) => {
                  // A composited rate is a weighted blend of several localities
                  // or utilities. Each component is its own filing, so it gets
                  // its own citation and the blend shows its arithmetic.
                  const comps = r.components || [];
                  const unitTotal = comps.reduce((a, c) => a + (c.units || 0), 0);
                  return (
                  <div key={key} className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800">{r.label || key}</span>
                      <span className="font-mono text-gray-700">{formatRate(r)}</span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      {r.source}
                      {r.effectiveLabel ? <> · {r.effectiveLabel}</> : r.effectiveDate ? <> · eff {formatDate(r.effectiveDate)}</> : null}
                    </div>
                    {r.sourceDetail && (
                      <div className="text-gray-500 mt-1 leading-snug">{r.sourceDetail}</div>
                    )}
                    {r.url && (
                      <a
                        href={r.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="inline-flex items-start gap-1 text-blue-600 hover:text-blue-800 mt-1 break-words"
                      >
                        <ExternalLink className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        <span>{r.urlLabel || 'source'}</span>
                      </a>
                    )}
                    {(r.links || []).map((l) => (
                      <a
                        key={l.url}
                        href={l.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-start gap-1 text-blue-600 hover:text-blue-800 mt-1 break-words"
                      >
                        <ExternalLink className="h-3 w-3 flex-shrink-0 mt-0.5" />
                        <span>{l.label}</span>
                      </a>
                    ))}
                    {comps.length > 0 && (
                      <div className="mt-2 border-t border-gray-200 pt-2 space-y-1.5">
                        <div className="font-semibold text-gray-600">
                          Weighted from {comps.length} sources across {unitTotal} leased units
                        </div>
                        {comps.map((c) => {
                          const share = unitTotal ? Math.round((c.units / unitTotal) * 1000) / 10 : 0;
                          return (
                            <div key={c.key} className="pl-2 border-l-2 border-gray-200">
                              <div className="font-medium text-gray-700">
                                {c.label}{' '}
                                <span className="font-mono">{c.value}{r.unit}</span>{' '}
                                <span className="text-gray-500">&times; {c.units} unit{c.units === 1 ? '' : 's'} ({share}%)</span>
                              </div>
                              {c.note && <div className="text-gray-500 mt-0.5 leading-snug">{c.note}</div>}
                              {c.url ? (
                                <a
                                  href={c.url}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-0.5 break-all"
                                >
                                  <ExternalLink className="h-3 w-3 flex-shrink-0" /> source
                                </a>
                              ) : (
                                <div className="text-gray-400 italic mt-0.5">
                                  Not published online — the note above identifies the document this figure comes from.
                                </div>
                              )}
                            </div>
                          );
                        })}
                        <div className="text-gray-600 font-mono break-all pt-1 leading-snug">
                          ({comps.map((c) => `${c.value}\u00d7${c.units}`).join(' + ')}) &divide; {unitTotal} = {r.value}{r.unit}
                        </div>
                      </div>
                    )}
                  </div>
                  );
                })}
                {data.ahdd && (
                  <div className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                    <div className="flex items-center justify-between gap-2">
                      <span className="font-semibold text-gray-800">Climate (degree days)</span>
                      <span className="font-mono text-gray-700">{data.ahdd}{data.acdd ? ` · ${data.acdd}` : ''}</span>
                    </div>
                    <div className="text-gray-500 mt-1">
                      HUD Utility Schedule Model (Ver. 13i) per-PHA climate dataset — 30-year normalized NOAA heating and cooling degree days for this authority's HUD code (HUD PD&amp;R) · 30-yr normals
                    </div>
                    <a
                      href="https://www.huduser.gov/portal/resources/utilallowance.html"
                      target="_blank"
                      rel="noopener noreferrer"
                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1 break-all"
                    >
                      <ExternalLink className="h-3 w-3 flex-shrink-0" /> source
                    </a>
                  </div>
                )}
              </div>
            )}
          </div>
        )}

        {/* Appliance / service source panel (collapsible) — flat rows priced
            from retailers; format mirrors the rate panel above exactly */}
        {(() => {
          const flats = (data.categories?.[0]?.rows || []).filter((r) => !r.rateKey && r.flatSource);
          if (!flats.length) return null;
          return (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <button
                onClick={() => setShowApplianceSources((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {showApplianceSources ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                Refrigerator &amp; range sources ({flats.length}) — every figure traceable
              </button>
              {showApplianceSources && (
                <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2">
                  {flats.map((row) => {
                    const fs = row.flatSource;
                    const v = row.values.find((x) => x != null);
                    return (
                      <div key={row.label} className="text-xs bg-gray-50 rounded-lg p-2.5 border border-gray-100">
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-semibold text-gray-800">{row.label}</span>
                          <span className="font-mono text-gray-700">{v != null ? `$${v}/mo` : '—'}</span>
                        </div>
                        <div className="text-gray-500 mt-1">
                          {fs.source}
                          {fs.effectiveDate && <> · priced {formatDate(fs.effectiveDate)}</>}
                        </div>
                        {Array.isArray(fs.links) && fs.links.map((l) => (
                          <a
                            key={l.url}
                            href={l.url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="flex items-center gap-1 text-blue-600 hover:text-blue-800 mt-1"
                          >
                            <ExternalLink className="h-3 w-3 flex-shrink-0" /> {l.label}
                          </a>
                        ))}
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          );
        })()}
        {/* 982.517 annual review — the 10% test (collapsible; only for orgs
            with a prior-schedule baseline in the compliance config) */}
        {data.tenPercentReview && data.tenPercentReview.rows?.length > 0 && (() => {
          const r = data.tenPercentReview;
          const colors = { ok: 'text-green-700', breach: 'text-red-700', pending: 'text-amber-700', first: 'text-gray-500' };
          const verdicts = { ok: 'No revision required', breach: 'REVISION REQUIRED', pending: 'Pending', first: 'No comparison available' };
          const kept = r.rows.filter((x) => x.status === 'ok').length;
          return (
            <div className="mt-3 border-t border-gray-100 pt-3">
              <button
                onClick={() => setShowReview((v) => !v)}
                className="flex items-center gap-1.5 text-sm font-medium text-gray-700 hover:text-gray-900"
              >
                {showReview ? <ChevronDown className="h-4 w-4" /> : <ChevronRight className="h-4 w-4" />}
                {r.firstCycle
                  ? `First schedule — no prior schedule to compare (${r.rows.length} rates set as next year's baseline)`
                  : `10% Rule Review (${kept} of ${r.rows.length} categories kept)`} — 24 CFR § 982.517
              </button>
              {showReview && (
                <div className="mt-2 space-y-2">
                  <p className="text-xs text-gray-500">
                    {r.firstCycle ? (
                      <>
                        The rule requires revising a category only when its utility rate moved 10% or more
                        since the last revision, which compares a schedule against the one before it, so it
                        does not apply to a first schedule. {r.baselineNote}
                      </>
                    ) : (
                      <>
                        The rule requires revising a category only when its utility rate moved 10% or more
                        since the last revision — everything under the threshold may lawfully be kept.
                        Prior schedule: {r.priorSource}{r.baselineNote ? ` ${r.baselineNote}` : ''}
                      </>
                    )}
                  </p>
                  <div className="overflow-x-auto border border-gray-100 rounded-lg">
                    <table className="w-full text-xs">
                      <thead>
                        <tr className="bg-gray-50 text-left text-gray-600">
                          <th className="px-3 py-2 font-semibold">Category</th>
                          <th className="px-3 py-2 font-semibold text-right">Prior</th>
                          <th className="px-3 py-2 font-semibold text-right">Current</th>
                          <th className="px-3 py-2 font-semibold text-right">Change</th>
                          <th className="px-3 py-2 font-semibold">Determination</th>
                        </tr>
                      </thead>
                      <tbody>
                        {r.rows.map((row) => (
                          <tr key={row.key} className="border-t border-gray-100 align-top">
                            <td className="px-3 py-2">
                              <div className="font-medium text-gray-800">{row.label}</div>
                              {row.priorBasis && <div className="text-gray-400 mt-0.5">Prior: {row.priorBasis}</div>}
                              {row.currentNote && <div className="text-gray-400 mt-0.5">Current: {row.currentNote}</div>}
                              {row.note && <div className="text-amber-700/80 mt-0.5">{row.note}</div>}
                            </td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{row.prior != null ? `$${formatRateValue(row.prior)}${row.unit}` : '—'}</td>
                            <td className="px-3 py-2 text-right tabular-nums whitespace-nowrap">{row.current != null ? `$${formatRateValue(row.current)}${row.unit}` : '—'}</td>
                            <td className={`px-3 py-2 text-right font-semibold tabular-nums whitespace-nowrap ${colors[row.status] || 'text-gray-600'}`}>
                              {row.pctChange != null ? `${row.pctChange > 0 ? '+' : ''}${row.pctChange}%`
                                : row.status === 'breach' ? '10%+ (documented)' : '—'}
                            </td>
                            <td className={`px-3 py-2 font-semibold whitespace-nowrap ${colors[row.status] || 'text-gray-600'}`}>
                              {verdicts[row.status] || ''}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </div>
              )}
            </div>
          );
        })()}
      </div>

      {/* Adopted / computed toggle + carried legend (keep-mode orgs only) */}
      {data.adopted?.categories?.length > 0 && (
        <div className="flex flex-wrap items-center gap-3 px-1">
          <div className="flex gap-1 bg-gray-100 rounded-lg p-1">
            <button
              onClick={() => setScheduleView('adopted')}
              className={`px-3 py-1 rounded text-xs font-medium ${scheduleView === 'adopted' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Adopted Schedule
            </button>
            <button
              onClick={() => setScheduleView('computed')}
              className={`px-3 py-1 rounded text-xs font-medium ${scheduleView === 'computed' ? 'bg-white text-blue-700 shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              Computed Reference
            </button>
          </div>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full border-collapse text-sm">
            <caption className="sr-only">
              {data.title} for {data.locality}, {data.unitsLabel}
            </caption>
            <thead>
              <tr className="bg-gray-50 border-b border-gray-200">
                <th
                  scope="col"
                  className="sticky left-0 z-10 bg-gray-50 text-left font-semibold text-gray-700 px-4 py-2.5 min-w-[200px]"
                >
                  Unit Type
                </th>
                {bedrooms.map((b) => (
                  <th
                    key={b}
                    scope="col"
                    className="text-center font-semibold text-gray-700 px-3 py-2.5 whitespace-nowrap min-w-[56px]"
                  >
                    {b}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {((data.adopted?.categories?.length > 0 && scheduleView === 'adopted')
                ? data.adopted.categories
                : data.categories
              ).map((cat) => (
                <React.Fragment key={cat.unitType}>
                  {/* Category header row */}
                  <tr className="bg-gray-100/70 border-t border-gray-200">
                    <th
                      scope="colgroup"
                      colSpan={nCols + 1}
                      className="sticky left-0 text-left font-bold text-gray-800 px-4 py-1.5 bg-gray-100/70"
                    >
                      {cat.unitType}
                      {cat.priorTypes?.length > 0 && (
                        <span className="ml-2 font-normal text-[11px] text-gray-400">
                          (prior schedule: {cat.priorTypes.join(' · ')})
                        </span>
                      )}
                    </th>
                  </tr>
                  {/* Fuel / service rows */}
                  {cat.rows.map((row, ri) => {
                    const rowKey = `${cat.unitType}-${row.label}`;
                    const rate = row.rateKey ? rates[row.rateKey] : null;
                    const flatSource = row.flatSource || null;
                    // carried rows keep prior-schedule values, so our rate
                    // math doesn't derive them — no calculator toggle
                    // scaled rows (rate-scale revision) are prior x rate
                    // change, not the model math either
                    const canExplain = (!!rate || !!flatSource) && !row.carried && !row.scaled;
                    const isOpen = openRows.has(rowKey);
                    return (
                      <React.Fragment key={rowKey}>
                        <tr
                          className={`border-t border-gray-100 ${ri % 2 ? 'bg-white' : 'bg-gray-50/40'} hover:bg-blue-50/40`}
                        >
                          <th
                            scope="row"
                            className="sticky left-0 z-10 text-left font-normal text-gray-700 pl-8 pr-4 py-2 bg-inherit"
                          >
                            <span className="inline-flex items-center gap-1.5">
                              {row.label}
                              {row.carried && (
                                <span
                                  title={row.carriedWhy === 'pending'
                                    ? 'Retained pending the seasonal rate publication'
                                    : 'Retained from the prior adopted schedule — rate change under 10% (24 CFR § 982.517)'}
                                  className="text-blue-600 font-semibold cursor-help"
                                >†</span>
                              )}
                              {row.scaled && (
                                <span
                                  title={`Prior adopted amount revised by this category's rate change (× ${row.scaleFactor}), 24 CFR § 982.517(c)(1)`}
                                  className="text-blue-600 font-semibold cursor-help"
                                >‡</span>
                              )}
                              {canExplain && (
                                <button
                                  onClick={() => toggleRow(rowKey)}
                                  title="Show source + math"
                                  className={`inline-flex items-center rounded p-0.5 ${isOpen ? 'text-blue-700 bg-blue-50' : 'text-gray-400 hover:text-blue-600 hover:bg-gray-100'}`}
                                >
                                  <Calculator className="h-3.5 w-3.5" />
                                </button>
                              )}
                            </span>
                          </th>
                          {row.values.map((v, vi) => (
                            <td key={vi} className="text-center px-3 py-2 text-gray-800 tabular-nums">
                              {v == null ? <span className="text-gray-300">—</span> : v}
                            </td>
                          ))}
                        </tr>

                        {/* Expandable calculation row — flat locally-priced allowance.
                            Formula only; sources live in the panel up top. */}
                        {flatSource && !rate && isOpen && (
                          <tr className="bg-blue-50/30 border-t border-blue-100">
                            <td colSpan={nCols + 1} className="px-8 py-3">
                              <div className="text-xs text-gray-700 space-y-1.5">
                                {flatSource.calc ? (
                                  <div className="font-mono">
                                    {flatSource.calc.taxRatePct != null ? (
                                      <>(${flatSource.calc.avgPrice.toFixed(2)} avg price + {flatSource.calc.taxRatePct}% {flatSource.calc.taxState} sales tax
                                      {' '}= ${flatSource.calc.taxedPrice.toFixed(2)}) ÷ ({flatSource.calc.lifeYears} yr × 12 mo)
                                      {' '}= ${flatSource.calc.monthlyExact.toFixed(2)} → ${row.values.find((x) => x != null)}/mo</>
                                    ) : (
                                      <>${flatSource.calc.avgPrice.toFixed(2)} avg price ÷ ({flatSource.calc.lifeYears} yr × 12 mo)
                                      {' '}= ${flatSource.calc.monthlyExact.toFixed(2)} → ${row.values.find((x) => x != null)}/mo</>
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-gray-500">
                                    Flat monthly allowance the utility model does not compute — set from local
                                    pricing and applied uniformly across bedroom sizes.
                                  </div>
                                )}
                                {flatSource.source && (
                                  <div className="text-gray-500">
                                    {flatSource.source}
                                    {flatSource.effectiveDate && <> · priced {formatDate(flatSource.effectiveDate)}</>}
                                  </div>
                                )}
                                {Array.isArray(flatSource.links) && flatSource.links.length > 0 && (
                                  <ul className="space-y-0.5">
                                    {flatSource.links.map((l) => (
                                      <li key={l.url}>
                                        <a href={l.url} target="_blank" rel="noopener noreferrer"
                                          className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                          <ExternalLink className="h-3 w-3 flex-shrink-0" /> {l.label}
                                        </a>
                                      </li>
                                    ))}
                                  </ul>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}

                        {/* Expandable provenance + math row */}
                        {rate && isOpen && (
                          <tr className="bg-blue-50/30 border-t border-blue-100">
                            <td colSpan={nCols + 1} className="px-8 py-3">
                              <div className="text-xs text-gray-700 space-y-2">
                                <div className="flex flex-wrap items-center gap-2">
                                  <span className="font-semibold">Rate:</span>
                                  <span className="font-mono">{formatRate(rate)}</span>
                                  {(rate.effectiveLabel || rate.effectiveDate) && <span className="text-gray-500">· {rate.effectiveLabel || `eff ${formatDate(rate.effectiveDate)}`}</span>}
                                  <span className="text-gray-500">· {rate.source}</span>
                                  {rate.url && (
                                    <a href={rate.url} target="_blank" rel="noopener noreferrer"
                                      className="inline-flex items-center gap-1 text-blue-600 hover:text-blue-800">
                                      <ExternalLink className="h-3 w-3" /> source
                                    </a>
                                  )}
                                </div>
                                <div className="text-gray-500">
                                  Formula: {row.fixedApplied
                                    ? '(annual consumption × rate ÷ 12) + monthly customer charge = monthly allowance.'
                                    : '(annual consumption × rate) ÷ 12 = monthly allowance.'}
                                </div>
                                {Array.isArray(row.breakdown) && (
                                  <div className="overflow-x-auto">
                                    <table className="text-xs border-collapse mt-1">
                                      <thead>
                                        <tr className="text-gray-500">
                                          <th className="text-left pr-3 py-0.5 font-medium">Bedroom</th>
                                          <th className="text-right pr-3 py-0.5 font-medium">Annual use × rate ÷ 12</th>
                                          <th className="text-right pr-3 py-0.5 font-medium">{row.fixedApplied ? '+ customer chg' : ''}</th>
                                          <th className="text-right py-0.5 font-medium">= monthly</th>
                                        </tr>
                                      </thead>
                                      <tbody>
                                        {bedrooms.map((b, bi) => {
                                          const cell = row.breakdown[bi];
                                          if (!cell) return null;
                                          const energyMo = cell.native != null
                                            ? cell.native * rate.value / 12
                                            : null;
                                          return (
                                            <tr key={b} className="border-t border-blue-100/60">
                                              <td className="pr-3 py-0.5">{b}</td>
                                              <td className="text-right pr-3 py-0.5 tabular-nums">
                                                {cell.native != null
                                                  ? `${cell.native} ${cell.nativeUnit || ''} × $${rate.value}${rate.unit || ''} ÷ 12 = $${energyMo.toFixed(2)}`
                                                  : '—'}
                                              </td>
                                              <td className="text-right pr-3 py-0.5 tabular-nums text-gray-500">
                                                {row.fixedApplied ? `+ $${Number(row.fixedApplied).toFixed(2)}` : ''}
                                              </td>
                                              <td className="text-right py-0.5 tabular-nums font-medium">
                                                {cell.monthly != null ? `$${cell.monthly}` : '—'}
                                              </td>
                                            </tr>
                                          );
                                        })}
                                      </tbody>
                                    </table>
                                  </div>
                                )}
                              </div>
                            </td>
                          </tr>
                        )}
                      </React.Fragment>
                    );
                  })}
                </React.Fragment>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* HUD-52667 family allowance worksheet (interactive) */}
      <FamilyWorksheet data={data} />

      {/* Footnote */}
      <div className="flex items-start gap-2 text-xs text-gray-500 px-1">
        <FileText className="h-3.5 w-3.5 flex-shrink-0 mt-0.5" />
        <p>
          {data.sample && (
            <span className="font-semibold text-amber-700">
              Sample schedule — prepared for demonstration; not an adopted schedule of this housing authority.{' '}
            </span>
          )}
          {data.modelNote ? `${data.modelNote} ` : ''}
          Figures are HousingAI&rsquo;s computed monthly allowances using HUD&rsquo;s HUSM regressions and the cited rates above
          {effective ? `, as of ${effective}` : ''}. Your housing authority&rsquo;s officially adopted schedule governs.
          Click the calculator icon on any row to see its source and math. Dash (&mdash;) = no allowance set.
          {data.adopted?.categories?.length > 0 && (
            <>
              {' '}<span className="text-blue-600">†</span> = kept at the prior schedule&rsquo;s amount.
              The 10% rule (24 CFR § 982.517) only requires revising a category when its utility rate
              changes 10% or more &mdash; daggered allowances stay exactly as previously adopted.
              {data.adopted.categories.some((c) => (c.rows || []).some((rw) => rw.scaled)) && (
                <>
                  {' '}<span className="text-blue-600">‡</span> = prior amount revised by the category&rsquo;s
                  rate change (prior allowance &times; current rate &divide; prior rate; see the 10% test).
                </>
              )}
              {' '}Rows without <span className="text-blue-600">
                {data.adopted.categories.some((c) => (c.rows || []).some((rw) => rw.scaled)) ? '† or ‡' : '†'}
              </span> are newly computed values &mdash; the ones
              with the calculator icon, which shows each row&rsquo;s source and math.
              {data.adopted.categories.some((c) => (c.rows || []).some((rw) => rw.filledFromComputed)) &&
                ' Where a daggered row’s prior sheet did not price a bedroom size, that cell shows the computed value.'}
            </>
          )}
        </p>
      </div>

    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   FAMILY ALLOWANCE WORKSHEET — interactive HUD-52667 "Actual Family
   Allowances" block. Pick the unit type + bedroom size, check the utilities
   the tenant pays directly, and the total computes from the schedule —
   the same arithmetic a specialist does by hand on the paper form.
   ═══════════════════════════════════════════════════════════════════════════ */

const FamilyWorksheet = ({ data }) => {
  // families compute against the ADOPTED allowances when keep-mode is active
  const categories = data.adopted?.categories || data.categories || [];
  const bedrooms = data.bedrooms || [];
  const [open, setOpen] = useState(false);
  const [catIdx, setCatIdx] = useState(0);
  const [bedIdx, setBedIdx] = useState(0);
  const [checked, setChecked] = useState(() => new Set());

  const cat = categories[catIdx];
  if (!cat) return null;

  const toggle = (label) => {
    setChecked((prev) => {
      const next = new Set(prev);
      next.has(label) ? next.delete(label) : next.add(label);
      return next;
    });
  };

  const total = cat.rows.reduce((sum, row) => {
    if (!checked.has(row.label)) return sum;
    const v = row.values[bedIdx];
    return sum + (v == null ? 0 : Number(v));
  }, 0);

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      <button
        onClick={() => setOpen((v) => !v)}
        className="w-full flex items-center justify-between px-5 py-3.5 hover:bg-gray-50"
      >
        <span className="flex items-center gap-2 text-sm font-semibold text-gray-800">
          <span className="p-1.5 rounded-lg bg-blue-50"><ClipboardList className="h-4 w-4 text-blue-700" /></span>
          Family Allowance Worksheet
        </span>
        {open ? <ChevronDown className="h-4 w-4 text-gray-500" /> : <ChevronRight className="h-4 w-4 text-gray-500" />}
      </button>

      {open && (
        <div className="px-5 pb-5 border-t border-gray-100">
          <p className="text-xs text-gray-500 mt-3 mb-4">
            Check each utility or service the tenant pays directly. The allowance fills in from the
            schedule above for the selected unit type and bedroom size — the same lookup the paper
            form&rsquo;s &ldquo;Actual Family Allowances&rdquo; box asks a specialist to do by hand.
          </p>
          <div className="flex flex-wrap gap-3 mb-4">
            <label className="text-xs text-gray-600">
              <span className="block font-medium mb-1">Unit Type</span>
              <select
                value={catIdx}
                onChange={(e) => setCatIdx(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                {categories.map((c, i) => (
                  <option key={c.unitType} value={i}>{c.unitType}</option>
                ))}
              </select>
            </label>
            <label className="text-xs text-gray-600">
              <span className="block font-medium mb-1">Number of Bedrooms</span>
              <select
                value={bedIdx}
                onChange={(e) => setBedIdx(Number(e.target.value))}
                className="border border-gray-300 rounded px-2 py-1.5 text-sm bg-white"
              >
                {bedrooms.map((b, i) => (
                  <option key={b} value={i}>{b}</option>
                ))}
              </select>
            </label>
          </div>

          <div className="divide-y divide-gray-100 border border-gray-200 rounded-lg overflow-hidden">
            {cat.rows.map((row) => {
              const v = row.values[bedIdx];
              const isChecked = checked.has(row.label);
              return (
                <label
                  key={row.label}
                  className={`flex items-center justify-between gap-3 px-3 py-2 text-sm cursor-pointer ${isChecked ? 'bg-blue-50/60' : 'bg-white hover:bg-gray-50'}`}
                >
                  <span className="flex items-center gap-2.5 text-gray-700">
                    <input
                      type="checkbox"
                      checked={isChecked}
                      onChange={() => toggle(row.label)}
                      className="h-4 w-4 rounded border-gray-300 text-blue-600"
                    />
                    {row.label}
                  </span>
                  <span className={`tabular-nums ${v == null ? 'text-gray-300' : isChecked ? 'font-semibold text-gray-900' : 'text-gray-500'}`}>
                    {v == null ? '—' : `$${v}`}
                  </span>
                </label>
              );
            })}
            <div className="flex items-center justify-between px-3 py-2.5 bg-gray-100/80 text-sm font-bold text-gray-900">
              <span>Total Utility Allowance ({cat.unitType}, {bedrooms[bedIdx]})</span>
              <span className="tabular-nums">${total}</span>
            </div>
          </div>
          <p className="text-[11px] text-gray-400 mt-2">
            Rows marked &mdash; have no allowance set for this schedule. The downloaded PDF includes
            a printable copy of this worksheet.
          </p>
        </div>
      )}
    </div>
  );
};

/* ═══════════════════════════════════════════════════════════════════════════
   PRINT DOCUMENT — everything below only exists on paper.
   Invisible on screen; @media print hides #root and shows only this.
   ═══════════════════════════════════════════════════════════════════════════ */

const PRINT_CSS = `
#print-schedule { display: none; }

@media print {
  @page { size: letter; margin: 0.45in 0.5in 0.55in 0.5in; }

  body > #root { display: none !important; }

  #print-schedule {
    display: block;
    font-family: Helvetica, Arial, sans-serif;
    color: #0f172a;
    font-size: 8pt;
    -webkit-print-color-adjust: exact;
    print-color-adjust: exact;
  }

  #print-schedule .ps-mono { font-family: ui-monospace, Menlo, Consolas, monospace; font-size: 90%; }
  #print-schedule .ps-strong { font-weight: 700; }
  #print-schedule .ps-dim { color: #64748b; }
  #print-schedule .ps-r { text-align: right; }
  #print-schedule h2 { font-size: 12pt; margin: 0 0 5px; }
  #print-schedule h2.ps-mt { margin-top: 14px; }
  #print-schedule h3 { font-size: 9pt; margin: 10px 0 4px; border-bottom: 1px solid #cbd5e1; padding-bottom: 2px; }
  #print-schedule .ps-lede { font-size: 8pt; color: #334155; line-height: 1.5; margin: 0 0 7px; }
  #print-schedule .ps-runfoot { margin-top: 10px; padding-top: 4px; border-top: 0.6px solid #e2e8f0; font-size: 6.5pt; color: #64748b; }

  /* page 1 */
  #print-schedule .ps-page1 { break-after: page; page-break-after: always; }
  #print-schedule .ps-hd { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 2.5px solid #0f172a; padding-bottom: 6px; }
  #print-schedule .ps-brand { display: flex; align-items: center; gap: 6px; }
  #print-schedule .ps-logo { width: 24px; height: 24px; }
  #print-schedule .ps-wordmark { font-size: 13pt; font-weight: 700; color: #0f172a; }
  #print-schedule .ps-ai { color: #2D69EC; }
  #print-schedule .ps-hd-title { text-align: right; }
  #print-schedule .ps-hd-title h1 { font-size: 12.5pt; font-weight: 700; line-height: 1.15; margin: 0; }
  #print-schedule .ps-kicker { font-size: 7pt; letter-spacing: 1.5px; text-transform: uppercase; color: #2D69EC; font-weight: 700; margin-top: 2px; }
  #print-schedule .ps-meta { display: flex; border: 1px solid #cbd5e1; border-top: none; margin-bottom: 7px; }
  #print-schedule .ps-meta > div { flex: 1 1 auto; padding: 4px 8px; border-right: 1px solid #e2e8f0; }
  #print-schedule .ps-meta > div:last-child { border-right: none; }
  #print-schedule .ps-k { display: block; font-size: 5.8pt; text-transform: uppercase; letter-spacing: 0.8px; color: #64748b; }
  #print-schedule .ps-v { display: block; font-size: 8pt; margin-top: 1px; }
  #print-schedule table.ps-grid { width: 100%; border-collapse: collapse; }
  #print-schedule table.ps-grid th, #print-schedule table.ps-grid td { border: 0.6px solid #cbd5e1; padding: 1.6px 4px; font-size: 7.4pt; }
  #print-schedule table.ps-grid thead th { background: #0f172a; color: #fff; font-size: 7pt; }
  #print-schedule table.ps-grid th.ps-ut { text-align: left; width: 26%; }
  #print-schedule table.ps-grid th.ps-br { width: 8%; text-align: center; }
  #print-schedule table.ps-grid td { text-align: center; font-variant-numeric: tabular-nums; }
  #print-schedule tr.ps-cat td { background: #e8edf5; font-weight: 700; text-align: left; font-size: 7.4pt; }
  #print-schedule td.ps-fuel { text-align: left !important; padding-left: 14px !important; color: #334155; }
  #print-schedule .ps-p1foot { display: flex; justify-content: space-between; align-items: flex-end; gap: 12px; margin-top: 7px; padding-top: 5px; border-top: 1px solid #cbd5e1; }
  #print-schedule .ps-p1foot-l { font-size: 6.6pt; color: #475569; line-height: 1.45; max-width: 82%; }
  #print-schedule .ps-p1foot-r { font-size: 7.5pt; font-weight: 700; color: #2D69EC; white-space: nowrap; }

  /* pages 2+ */
  #print-schedule .ps-page { break-before: page; page-break-before: always; }

  /* HUD-52667 family allowance worksheet */
  #print-schedule .ps-ws-fields { display: flex; gap: 20px; margin: 8px 0 10px; }
  #print-schedule .ps-ws-fields > div { flex: 1; }
  #print-schedule .ps-ws-line { border-bottom: 0.8px solid #334155; height: 14px; }
  #print-schedule .ps-ws-cols { display: flex; gap: 16px; align-items: flex-start; }
  #print-schedule .ps-ws-cols > .ps-ws-tbl { flex: 0 0 58%; }
  #print-schedule table.ps-ws { width: 100%; border-collapse: collapse; }
  #print-schedule table.ps-ws th, #print-schedule table.ps-ws td { border: 0.6px solid #cbd5e1; padding: 3px 6px; font-size: 7.4pt; }
  #print-schedule table.ps-ws thead th { background: #0f172a; color: #fff; font-size: 7pt; }
  #print-schedule table.ps-ws td.ps-c { text-align: center; }
  #print-schedule tr.ps-ws-total td { background: #e8edf5; font-weight: 700; }
  #print-schedule .ps-ws-ut { font-size: 7.4pt; color: #334155; margin-bottom: 5px; }
  #print-schedule table.ps-src { width: 100%; border-collapse: collapse; margin-top: 2px; }
  #print-schedule table.ps-src th, #print-schedule table.ps-src td { border: 0.6px solid #cbd5e1; padding: 4px 6px; font-size: 7.6pt; text-align: left; vertical-align: top; }
  #print-schedule table.ps-src thead th { background: #0f172a; color: #fff; }
  #print-schedule .ps-url { color: #2D69EC; font-size: 6.6pt; word-break: break-all; }
  #print-schedule .ps-math-cols { column-count: 2; column-gap: 16px; }
  #print-schedule .ps-calc { margin-bottom: 7px; break-inside: avoid; page-break-inside: avoid; }
  #print-schedule .ps-calc-hd { display: flex; justify-content: space-between; gap: 10px; font-size: 7.6pt; background: #f1f5f9; border: 0.6px solid #cbd5e1; border-bottom: none; padding: 3px 6px; }
  #print-schedule table.ps-math { width: 100%; border-collapse: collapse; }
  #print-schedule table.ps-math th, #print-schedule table.ps-math td { border: 0.6px solid #cbd5e1; padding: 1.8px 6px; font-size: 7pt; text-align: left; }
  #print-schedule table.ps-math thead th { background: #e8edf5; font-size: 6.6pt; }
}
`;

const PrintLogo = () => (
  // Inline copy of public/LogoSVG_tb_bt.svg (transparent bg, black "AI") so the
  // print doc never depends on a network fetch. Keep in sync with that file.
  <svg viewBox="3.16 3.96 17.68 17.68" fill="none" className="ps-logo"
    xmlns="http://www.w3.org/2000/svg">
    <g stroke="#2D69EC" strokeWidth="1.68" strokeLinecap="round" strokeLinejoin="round">
      <path d="M4.8 20 L4.8 12.2" />
      <path d="M19.2 12.2 L19.2 20" />
      <path d="M4.8 20 L12 20" />
      <path d="M12 5.6 L4 12.8" />
      <path d="M12 5.6 L20 12.8" />
    </g>
    <text x="12" y="18.4" textAnchor="middle" fontFamily="Arial, sans-serif"
      fontWeight="bold" fontSize="9.6" fill="#000000">AI</text>
  </svg>
);

const PrintableSchedule = ({ data }) => {
  if (!data) return null;
  const { bedrooms = [], rates = {}, categories = [] } = data;
  const authority = data.authority || data.locality || '';
  const effective = formatDate(data.effectiveDate);
  const computed = formatDate(data.computedDate);
  const footerLine = `HousingAI · ${data.sample ? 'SAMPLE · ' : ''}Utility Allowance Schedule · ${authority}${effective ? ` · Effective ${effective}` : ''}`;

  const doc = (
    <div id="print-schedule" aria-hidden="true">
      <style>{PRINT_CSS}</style>

      {/* ------------ PAGE 1: standalone schedule ------------ */}
      <section className="ps-page1">
        <header className="ps-hd">
          <div className="ps-brand">
            <PrintLogo />
            <span className="ps-wordmark">Housing<span className="ps-ai">AI</span></span>
          </div>
          <div className="ps-hd-title">
            <h1>{data.title}</h1>
            <div className="ps-kicker" style={data.sample ? { color: '#b45309' } : undefined}>
              {data.sample ? 'Sample · Utility Allowance Schedule' : 'Utility Allowance Schedule'}
            </div>
          </div>
        </header>

        <div className="ps-meta">
          <div><span className="ps-k">Locality</span><span className="ps-v ps-strong">{authority}</span></div>
          <div><span className="ps-k">Provider</span><span className="ps-v">{data.provider}</span></div>
          {data.code && <div><span className="ps-k">PHA Code</span><span className="ps-v ps-mono">{data.code}</span></div>}
          {data.ahdd && <div><span className="ps-k">Climate</span><span className="ps-v">{data.ahdd}</span></div>}
          {effective && <div><span className="ps-k">Effective</span><span className="ps-v ps-strong">{effective}</span></div>}
        </div>

        <table className="ps-grid">
          <thead>
            <tr>
              <th className="ps-ut">Unit Type</th>
              {bedrooms.map((b) => <th key={b} className="ps-br">{b}</th>)}
            </tr>
          </thead>
          <tbody>
            {categories.map((cat) => (
              <React.Fragment key={cat.unitType}>
                <tr className="ps-cat"><td colSpan={bedrooms.length + 1}>{cat.unitType}</td></tr>
                {cat.rows.map((row) => (
                  <tr key={row.label}>
                    <td className="ps-fuel">{row.label}</td>
                    {row.values.map((v, i) => <td key={i}>{v == null ? '\u2014' : v}</td>)}
                  </tr>
                ))}
              </React.Fragment>
            ))}
          </tbody>
        </table>

        <div className="ps-p1foot">
          <div className="ps-p1foot-l">
            {data.modelNote ? `${data.modelNote} ` : ''}
            Every rate is sourced to its primary filing &mdash; see <b>Rate Sources (p.&nbsp;2)</b> and{' '}
            <b>Calculation Detail (pp.&nbsp;3+)</b> attached. Dash (&mdash;) = no allowance for that unit size.
            {computed && ` Generated ${computed}.`}
          </div>
          <div className="ps-p1foot-r">gethousingai.com</div>
        </div>
        <div className="ps-runfoot">{footerLine}</div>
      </section>

      {/* ------------ PAGE 2: rate sources + methodology ------------ */}
      <section className="ps-page">
        <h2>Rate Sources</h2>
        <p className="ps-lede">
          Every figure on the schedule (p.&nbsp;1) is computed from the rates below. No rate on this page is
          estimated or carried forward: each traces to a tariff filing, municipal rate schedule, or federal
          data series, with its own effective date.
        </p>
        <table className="ps-src">
          <thead><tr><th>Utility</th><th>Rate</th><th>Rate effective</th><th>Source</th></tr></thead>
          <tbody>
            {Object.entries(rates).map(([key, r]) => (
              <tr key={key}>
                <td className="ps-strong">{r.label || key}</td>
                <td className="ps-mono">{formatRate(r)}</td>
                <td>{formatDate(r.effectiveDate)}</td>
                <td>
                  {r.source}
                  {r.url && <><br /><span className="ps-url">{r.url}</span></>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>

        {(() => {
          const flats = (categories[0]?.rows || [])
            .filter((r) => !r.rateKey && (r.flatSource || r.values.some((v) => v != null)));
          if (!flats.length) return null;
          return (
            <>
              <h2 className="ps-mt">Local Services &amp; Appliance Pricing</h2>
              <p className="ps-lede">
                Flat monthly allowances the utility model does not compute: priced from local sources
                (area retailers for tenant-supplied appliances, amortized over their expected life; the
                municipal fee for trash) and applied uniformly across bedroom sizes.
              </p>
              <table className="ps-src">
                <thead><tr><th>Service / Appliance</th><th>Monthly</th><th>Priced</th><th>Local source</th></tr></thead>
                <tbody>
                  {flats.map((r) => {
                    const v = r.values.find((x) => x != null);
                    const fs = r.flatSource || {};
                    return (
                      <tr key={r.label}>
                        <td className="ps-strong">{r.label}</td>
                        <td>{v != null ? `$${v}` : '—'}</td>
                        <td>{formatDate(fs.effectiveDate)}</td>
                        <td>
                          {fs.source || 'Set by the housing authority.'}
                          {fs.calc && (
                            <><br /><span className="ps-mono ps-strong">
                              {fs.calc.taxRatePct != null ? (
                                <>(${fs.calc.avgPrice.toFixed(2)} avg price + {fs.calc.taxRatePct}% {fs.calc.taxState} sales tax
                                {' '}= ${fs.calc.taxedPrice.toFixed(2)}) ÷ ({fs.calc.lifeYears} yr × 12 mo)
                                {' '}= ${fs.calc.monthlyExact.toFixed(2)} → ${v}/mo</>
                              ) : (
                                <>${fs.calc.avgPrice.toFixed(2)} avg price ÷ ({fs.calc.lifeYears} yr × 12 mo)
                                {' '}= ${fs.calc.monthlyExact.toFixed(2)} → ${v}/mo</>
                              )}
                            </span></>
                          )}
                          {(fs.links || []).map((l) => (
                            <React.Fragment key={l.url}>
                              <br /><span className="ps-url">{l.label}: {l.url}</span>
                            </React.Fragment>
                          ))}
                          {!fs.links && fs.url && <><br /><span className="ps-url">{fs.url}</span></>}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </>
          );
        })()}

        <h2 className="ps-mt">Methodology</h2>
        <p className="ps-lede">
          Consumption for each unit type and bedroom size is produced by <b>HUD&rsquo;s Utility Schedule Model
          (HUSM), Ver.&nbsp;13i</b> &mdash; the same published RECS regression model HUD provides for schedule
          development &mdash; using the locality climate profile
          {data.ahdd ? ` (${data.ahdd}${data.acdd ? `, ${data.acdd}` : ''})` : ''}
          {data.ahdd ? ' — degree days taken verbatim from the HUSM workbook’s own per-PHA climate dataset (HUD PD&R, NOAA normals). Consultant schedules sometimes use other station normals, so their stated degree days may differ.' : '.'} Each monthly allowance is{' '}
          <span className="ps-mono">annual consumption &times; rate &divide; 12</span>, plus the provider&rsquo;s
          fixed monthly customer charge where applicable. The full plugged-in arithmetic for every cell on the
          schedule appears in the Calculation Detail pages that follow.
        </p>
        <p className="ps-lede">
          This schedule is prepared for annual review and adoption under <b>24 CFR &sect;&nbsp;982.517</b>. HUSM
          inputs, rates, and outputs are retained so the authority can document that the required review occurred,
          and a revision is triggered when any utility rate changes by 10% or more.
          {effective && ` Effective ${effective}.`}
        </p>
        <div className="ps-runfoot">{footerLine}</div>
      </section>

      {/* ------------ PAGES 3+: calculation detail ------------ */}
      <section className="ps-page">
        <h2>Calculation Detail</h2>
        <p className="ps-lede">
          The exact arithmetic behind every allowance on the schedule. Consumption figures are HUSM
          (Ver.&nbsp;13i) annual outputs for this locality&rsquo;s climate; rates are the cited filings on the Rate Sources page.
        </p>
        <div className="ps-math-cols">
          {categories.map((cat) => {
            const rows = cat.rows.filter((row) => row.rateKey && rates[row.rateKey]);
            if (!rows.length) return null;
            return (
              <div className="ps-calc-cat" key={cat.unitType}>
                <h3>{cat.unitType}</h3>
                {rows.map((row) => {
                  const rate = rates[row.rateKey];
                  const fixed = Number(row.fixedApplied || 0);
                  const bd = Array.isArray(row.breakdown) ? row.breakdown : null;
                  return (
                    <div className="ps-calc" key={row.label}>
                      <div className="ps-calc-hd">
                        <span className="ps-strong">{row.label}</span>
                        <span className="ps-mono ps-dim">
                          {formatRate(rate)}
                          {rate.effectiveDate && ` · eff ${formatDate(rate.effectiveDate)}`}
                          {rate.source && ` · ${rate.source}`}
                        </span>
                      </div>
                      <table className="ps-math">
                        <thead>
                          <tr>
                            <th>Bedroom</th>
                            <th className="ps-r">Annual consumption &times; rate &divide; 12</th>
                            <th className="ps-r">= Monthly</th>
                          </tr>
                        </thead>
                        <tbody>
                          {bd ? (
                            bedrooms.map((b, bi) => {
                              const cell = bd[bi];
                              if (!cell) return null;
                              const energy = (cell.native || 0) * rate.value / 12;
                              return (
                                <tr key={b}>
                                  <td>{b}</td>
                                  <td className="ps-mono ps-r">
                                    {cell.native} {cell.nativeUnit || ''} &times; ${rate.value}{rate.unit || ''} &divide; 12
                                    = ${energy.toFixed(2)}{fixed ? ` + $${fixed.toFixed(2)}` : ''}
                                  </td>
                                  <td className="ps-mono ps-r ps-strong">
                                    {cell.monthly != null ? `$${cell.monthly}` : '\u2014'}
                                  </td>
                                </tr>
                              );
                            })
                          ) : (
                            <tr>
                              <td colSpan={3} className="ps-mono">
                                Rate {formatRate(rate)} &mdash; (annual consumption &times; rate &divide; 12)
                                {fixed ? ' + fixed charge' : ''} = monthly allowance
                              </td>
                            </tr>
                          )}
                        </tbody>
                      </table>
                    </div>
                  );
                })}
              </div>
            );
          })}
        </div>
        <div className="ps-runfoot">{footerLine}</div>
      </section>
    </div>
  );

  return createPortal(doc, document.body);
};

export default UtilityAllowance;